﻿import { useState, useEffect } from 'react';
//Utilities
import { useQuery, useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
//Components
import { WizardButtons } from '../../Wizard/WizardButtons';
import { TitledCircularProgress } from '../../feedback/TitledCircularProgress/TitledCircularProgress';
import {
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  styled,
  Paper,
  TextField,
  Button,
  ButtonGroup,
} from '@mui/material';
import {
  GetOwnershipChangeInformation,
  GenerateTransactionID,
  GetOwnershipTransactionInformation,
  RequestOwnershipChangeLetter,
} from '../../../../../api/ownershipchange';
//Icons
import EmailIcon from '@mui/icons-material/Email';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MarkunreadMailboxIcon from '@mui/icons-material/MarkunreadMailbox';
//Styles
import './ownershipStyles.css';

//TODOs
//TODO: Fix Phone validation
//TODO: Trigger Email or Text API at AirKit
//TODO: Handle errors on generateTransactionID and requestOwnershipChangeLetter queries

const HowToReceive = (props) => {
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerPhone, setOwnerPhone] = useState('');
  const [estateTrust, setEstateTrust] = useState(null);
  const [receiveType, setReceiveType] = useState('email');

  const {
    register,
    trigger,
    formState: { errors },
  } = useForm();

  const generateTransactionID = useMutation({
    mutationFn: (data) => GenerateTransactionID(data)(),
  });

  const requestOwnershipChangeLetter = useMutation({
    mutationFn: (data) => RequestOwnershipChangeLetter(data)(),
  });

  const handleChange = (e) => {
    setReceiveType(e.target.value);
  };

  const getOwnershipInfo = useQuery({
    queryKey: ['getOwnershipInfo', props.referenceID],
    queryFn: () => GetOwnershipChangeInformation({ PolicyID: props.referenceID })(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: !!props.referenceID,
  });

  useEffect(() => {
    if (getOwnershipInfo.isSuccess && getOwnershipInfo.data) {
      //store the data in session
      window.sessionStorage.setItem('currentOwner', JSON.stringify(getOwnershipInfo.data));
    }
  }, [getOwnershipInfo.isSuccess, getOwnershipInfo.data]);

  useEffect(() => {
    if (getOwnershipInfo.data.isJuvenile)
      //if this is a juvenile, immediately go to paper
      setReceiveType('paper');
  }, [getOwnershipInfo.data]);

  const ValidateForm = () => {
    trigger().then((d) => {
      clickNext(d);
    });
  };

  const clickNext = (val) => {
    if (receiveType === 'paper') {
      requestOwnershipChangeLetter.mutate(
        {
          ReferenceID: props.referenceID,
          Source: null,
        },
        {
          onSuccess: (result) => {
            if (result) {
              props.nextHandler();
            }
          },
        }
      );
    } else {
      //check for an error
      let error = nullOrmessage();
      if (error == null) {
        if (errors && errors.ownerEmail) {
          error = error.ownerEmail.message;
        } else {
          error = null;
        }
      }
      if (val || error == null) {
        //  saveValues();
        generateTransactionID.mutate(
          {
            PolicyID: props.referenceID,
            TransactionTypeID: 3,
            PhoneNumber: phoneOrEmptyString(receiveType),
            Email: emailOrEmptyString(receiveType),
          },
          {
            onSuccess: (transactionID) => {
              //Transaction ID acquired
              if (!transactionID.includes('Error')) {
                props.nextHandler();
              }
            },
          }
        );
      }
    }
  };

  function nullOrmessage() {
    let message = null;
    if (errors && errors.ownerPhone) {
      message = errors.ownerPhone.message;
    }
    return message;
  }

  function phoneOrEmptyString(data) {
    let text = '';
    if (data === 'text') {
      text = ownerPhone;
    }
    return text;
  }
  function emailOrEmptyString(data) {
    let text = '';
    if (data === 'email') {
      text = ownerEmail;
    }
    return text;
  }

  const isBoolean = (ownerEmail) => {
    if (ownerEmail) {
      return true;
    } else {
      return false;
    }
  };
  const renderErrorText = () => {
    if (isBoolean(errors.ownerEmail)) {
      return errors.ownerEmail.message;
    } else {
      return null;
    }
  };
  function GetReceiveTypeButton() {
    switch (receiveType) {
      case 'email':
        return (
          <OwnerFormItem>
            <span className="formLabel">Email Address:</span>
            <TextField
              {...register('ownerEmail')}
              error={isBoolean(errors.ownerEmail)}
              helperText={renderErrorText()}
              className="fullwidth"
              value={ownerEmail}
              onChange={(e) => {
                setOwnerEmail(e.target.value);
              }}
            />
          </OwnerFormItem>
        );
      case 'text':
        return (
          <OwnerFormItem>
            <span className="formLabel">Phone Number:</span>
            <TextField
              {...register('ownerPhone')}
              error={isBoolean(errors.ownerPhone)}
              helperText={nullOrmessage()}
              className="fullwidth"
              value={ownerPhone}
              onChange={(e) => {
                setOwnerPhone(e.target.value);
              }}
            />
          </OwnerFormItem>
        );
      case 'paper':
        return <Alert severity="info">Are you sure you would like to receive by mail?</Alert>;
    }
  }

  if (getOwnershipInfo.isPending || getOwnershipInfo.isFetching)
    return <TitledCircularProgress title="Checking Policy" />;

  if (getOwnershipInfo.data.isJuvenile || estateTrust === true)
    return (
      <>
        <ToggleButtonGroup
          color="primary"
          value={receiveType}
          exclusive
          onChange={handleChange}
          style={{ width: '100%', justifyContent: 'center', margin: '1em 0' }}
        >
          <ToggleButton value="paper">
            <MarkunreadMailboxIcon color="primary" />
            Paper
          </ToggleButton>
        </ToggleButtonGroup>

        <WizardButtons {...props} nextHandler={ValidateForm} />
      </>
    );

  if (estateTrust == null) {
    return (
      <div style={{ textAlign: 'center' }}>
        <div>Will you be assigning ownership to an Estate or Trust?</div>
        <ButtonGroup style={{ width: '100%', justifyContent: 'center', margin: '1em 0' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              setEstateTrust(true);
              setReceiveType('paper');
            }}
          >
            Yes
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => {
              setEstateTrust(false);
            }}
          >
            No
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  return (
    <>
      <div>
        {' '}
        How would you like to receive your paperwork for: {`${props.referenceID}?`} Please select
        one.
      </div>
      <ToggleButtonGroup
        color="primary"
        value={receiveType}
        exclusive
        onChange={handleChange}
        style={{ width: '100%', justifyContent: 'center', margin: '1em 0' }}
      >
        {estateTrust === false && (
          <ToggleButton value="email">
            <EmailIcon color="primary" />
            Email
          </ToggleButton>
        )}
        {estateTrust === false && (
          <ToggleButton value="text">
            <PhoneIphoneIcon color="primary" />
            Text
          </ToggleButton>
        )}
        <ToggleButton value="paper">
          <MarkunreadMailboxIcon color="primary" />
          Paper
        </ToggleButton>
      </ToggleButtonGroup>

      {GetReceiveTypeButton()}

      <WizardButtons {...props} nextHandler={ValidateForm} />
    </>
  );
};
const decideColor = (theme) => {
  let color = '';
  if (theme.palette.mode === 'dark') {
    color = '#1A2027';
  } else {
    color = '#FFF';
  }
};
const OwnerFormItem = styled(Paper)(({ theme }) => ({
  backgroundColor: decideColor(theme),
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'left',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '.25em',
  color: theme.palette.text.secondary,
}));

export { HowToReceive };
